<template>
  <div>
    <ComponentHeader
      title="Address Roster Report"
      subtitle="Derived practice addresses per TIN. Generate re-runs the derivation and adds anything new as pending — it never changes an address already under review."
    >
      <button class="btn-primary-sm" :disabled="loading" @click="generate">
        {{ loading ? 'Generating…' : 'Generate / Refresh' }}
      </button>
    </ComponentHeader>

    <div class="p-5">
    <!-- Error -->
    <div v-if="error" class="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
      {{ error }}
    </div>

    <!-- Last generate summary -->
    <div
      v-if="lastRun"
      class="mb-4 p-4 bg-blue-100 border border-blue-300 rounded-lg text-sm text-blue-800"
    >
      Derived {{ lastRun.candidates }} candidate addresses ·
      <strong>{{ lastRun.created }} new</strong> saved as pending
      <span v-if="lastRun.already_on_file">
        · {{ lastRun.already_on_file }} already on file, left untouched
      </span>
      <span v-if="lastRun.duplicates_merged">
        · {{ lastRun.duplicates_merged }} duplicate candidates merged
      </span>
      <span v-if="lastRun.tins_truncated">
        · {{ lastRun.tins_truncated }} TINs have more locations than the cap shows
      </span>
      <span v-if="lastRun.tins_unresolved">
        · {{ lastRun.tins_unresolved }} TINs could not be matched to an entity
      </span>
    </div>

    <!-- Empty state -->
    <div
      v-if="!report && !initialLoading && !loading && !error"
      class="py-12 text-center text-sm text-gray-500"
    >
      No addresses stored for this tenant yet. Click "Generate / Refresh" to derive
      them from claims and the provider directory.
    </div>

    <!-- Loading -->
    <LoadingIndicator
      v-if="loading && !report"
      :message="`Resolving TIN addresses… (${elapsedSeconds}s)`"
    />

    <template v-if="report">
      <!-- Controls -->
      <div class="flex items-end gap-4 mb-4 flex-wrap">
        <div class="grow max-w-xs">
          <label class="block text-xs font-medium text-gray-700 mb-1">Search</label>
          <input
            v-model="search"
            type="text"
            placeholder="TIN, practice, city…"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          />
        </div>
        <FilterDropdown
          v-model="statusFilter"
          label="Status"
          :options="statusOptions"
          placeholder="All statuses"
          clearable
        />
        <button
          v-if="search || statusFilter"
          class="text-xs text-blue-600 hover:underline pb-2"
          @click="resetFilters"
        >
          Reset
        </button>
        <div class="ml-auto flex items-end gap-4 pb-1">
          <span class="text-xs text-gray-600">
            {{ filteredTins.length.toLocaleString() }} addresses
          </span>
          <ExportMenu :disabled="!filteredTins.length" :exports="[{ type: 'csv', action: exportCsv }]" />
        </div>
      </div>

      <!-- Table -->
      <div class="border border-gray-200 rounded-lg overflow-auto" style="max-height: calc(100vh - 340px)">
        <table class="w-full text-sm">
          <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700 transition-colors"
                @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="sortKey === col.key" class="ml-0.5 text-primary-500">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredTins"
              :key="row.id"
              class="hover:bg-blue-50/30"
              :class="row.isFirstOfTin ? 'border-t border-gray-200' : ''"
            >
              <td class="px-4 py-3 font-mono text-xs text-gray-800 whitespace-nowrap align-top">
                {{ row.isFirstOfTin ? row.tin : '' }}
              </td>
              <!-- Practice names wrap rather than truncate — they are readable
                   content, and truncating them is an explicit anti-pattern. -->
              <td class="px-4 py-3 align-top">
                <template v-if="row.isFirstOfTin">
                  <router-link
                    v-if="practiceLink(row)"
                    :to="practiceLink(row)"
                    class="text-gray-900 font-medium hover:text-neutral-700 hover:underline"
                  >
                    {{ row.practice_name }}
                  </router-link>
                  <span v-else class="text-gray-900 font-medium">{{ row.practice_name }}</span>
                  <div class="flex items-center gap-2 mt-1 flex-wrap">
                    <Tag
                      v-if="row.tinCount > 1"
                      :label="`${row.tinCount} locations`"
                      color="blue"
                    />
                    <button
                      v-if="row.practice_entity"
                      class="text-xs text-gray-600 hover:text-gray-800 hover:underline"
                      @click="$emit('preview-practice', row.practice_entity)"
                    >
                      Practice view →
                    </button>
                  </div>
                </template>
              </td>
              <td class="px-3 py-3 text-right text-gray-800 align-top tabular-nums">
                {{ row.isFirstOfTin ? row.provider_count : '' }}
              </td>
              <td class="px-3 py-3 text-gray-800 align-top">
                <template v-if="row.address_line1">
                  <div>{{ row.address_line1 }}<span v-if="row.address_line2">, {{ row.address_line2 }}</span></div>
                  <div class="text-xs text-gray-600">{{ row.city }}, {{ row.state }} {{ row.zip_code }}</div>
                </template>
                <span v-else class="text-xs text-yellow-800">No address found — ask the practice</span>
              </td>
              <td class="px-3 py-3 text-gray-800 align-top">{{ row.county || '—' }}</td>
              <td class="px-3 py-3 align-top">
                <Tag :label="statusLabel(row.status)" :color="statusColor(row.status)" />
                <div v-if="row.edited" class="mt-1">
                  <Tag label="Corrected" color="blue" />
                </div>
                <div v-if="row.practice_reviewer_name" class="text-xs text-gray-600 mt-1">
                  {{ row.practice_reviewer_name }}
                </div>
              </td>
            </tr>
            <tr v-if="filteredTins.length === 0">
              <td :colspan="columns.length" class="px-4 py-12 text-center text-sm text-gray-500">
                No addresses match your filters. Clear the search or status filter to
                see all {{ (report || []).length.toLocaleString() }} addresses.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { canAccessEntity } from '@/utils/access'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Confirmed',
  rejected: 'Rejected',
  superseded: 'Replaced',
}
const STATUS_COLORS = {
  pending: 'amber',
  approved: 'emerald',
  rejected: 'red',
  superseded: 'gray',
}

export default {
  name: 'AddressRosterReport',
  components: { ComponentHeader, LoadingIndicator, FilterDropdown, Tag, ExportMenu },
  emits: ['preview-practice'],
  data() {
    return {
      loading: false,
      error: null,
      report: null,
      initialLoading: true,
      lastRun: null,
      elapsedTimer: null,
      elapsedSeconds: 0,
      search: '',
      statusFilter: null,
      sortKey: 'provider_count',
      sortDir: 'desc',
      columns: [
        { key: 'tin', label: 'TIN' },
        { key: 'practice_name', label: 'Practice' },
        { key: 'provider_count', label: 'Providers at TIN' },
        { key: 'address_line1', label: 'Address' },
        { key: 'county', label: 'County' },
        { key: 'status', label: 'Status' },
      ],
      statusOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Confirmed' },
        { value: 'rejected', label: 'Rejected' },
      ],
    }
  },
  computed: {
    filteredTins() {
      if (!this.report) return []
      let rows = this.report
      if (this.statusFilter) rows = rows.filter(r => r.status === this.statusFilter)
      const q = this.search.trim().toLowerCase()
      if (q) {
        rows = rows.filter(r =>
          String(r.tin).includes(q) ||
          (r.practice_name || '').toLowerCase().includes(q) ||
          (r.city || '').toLowerCase().includes(q)
        )
      }
      // Sort GROUPS, not rows. A row-level secondary key cannot hold a TIN
      // together: for Address/County/Status every row in a TIN differs, so the
      // primary comparison always decides and the group tiebreak never runs —
      // which scattered a TIN's addresses across the table. Sorting whole groups
      // makes adjacency structural instead of a lucky side effect of the default
      // sort. Each row is emitted with its own isFirstOfTin/tinCount so the
      // template needs no per-row lookups.
      const dir = this.sortDir === 'asc' ? 1 : -1
      const k = this.sortKey

      const groups = []
      const byTin = new Map()
      for (const r of rows) {
        let g = byTin.get(r.tin)
        if (!g) {
          g = { tin: r.tin, rows: [] }
          byTin.set(r.tin, g)
          groups.push(g)
        }
        g.rows.push(r)
      }

      // Compare on the group's smallest value for the chosen column, so a group
      // sorts by its best row. Coerce consistently — provider_count is nullable,
      // and mixing numeric and string comparison made the old comparator
      // non-transitive.
      const numeric = rows.some(r => typeof r[k] === 'number')
      const keyOf = (g) => {
        const vals = g.rows.map(r => r[k])
        return numeric
          ? Math.min(...vals.map(v => (typeof v === 'number' ? v : Number.NEGATIVE_INFINITY)))
          : vals.map(v => String(v ?? '')).sort()[0]
      }
      const cmp = numeric
        ? (a, b) => dir * (keyOf(a) - keyOf(b))
        : (a, b) => dir * String(keyOf(a)).localeCompare(String(keyOf(b)))

      groups.sort((a, b) => cmp(a, b) || String(a.tin ?? '').localeCompare(String(b.tin ?? '')))

      const out = []
      for (const g of groups) {
        const ordered = [...g.rows].sort((a, b) =>
          String(a.address_line1 ?? '').localeCompare(String(b.address_line1 ?? ''))
        )
        ordered.forEach((r, i) => {
          out.push({ ...r, isFirstOfTin: i === 0, tinCount: ordered.length })
        })
      }
      return out
    },
  },
  mounted() {
    this.load()
  },
  beforeUnmount() {
    this.stopTimers()
  },
  methods: {
    // Canonical entity route, gated by the same access check the AG-Grid
    // entity-link cell uses. Null when the user cannot open that entity.
    practiceLink(row) {
      if (!row.practice_entity || !canAccessEntity(row.practice_entity)) return null
      return `/entity/${row.practice_entity}`
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'gray'
    },
    resetFilters() {
      this.search = ''
      this.statusFilter = null
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
    },
    stopTimers() {
      if (this.elapsedTimer) { clearInterval(this.elapsedTimer); this.elapsedTimer = null }
    },
    // Read what is stored. Fast — this is Postgres, not Snowflake.
    async load() {
      try {
        // No page_size: the viewset is unpaginated, so the param was ignored and
        // implied a limit that did not exist. The full set is needed anyway —
        // search, sort and TIN grouping all run client-side.
        const { data } = await axios.get('/api/rostering/addresses/')
        this.report = data.results || data
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to load addresses'
      } finally {
        this.initialLoading = false
      }
    },

    // Re-run the Snowflake derivation and persist anything new as pending.
    async generate() {
      this.loading = true
      this.error = null
      this.elapsedSeconds = 0
      this.elapsedTimer = setInterval(() => { this.elapsedSeconds++ }, 1000)
      try {
        const { data } = await axios.post('/api/rostering/addresses/generate/')
        this.lastRun = data
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to generate'
      } finally {
        this.loading = false
        this.stopTimers()
      }
    },
    exportCsv() {
      const headers = this.columns.map(c => c.label)
      const keys = this.columns.map(c => c.key)
      const escape = v => {
        const s = String(v ?? '')
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const lines = [headers.join(',')]
      for (const r of this.filteredTins) {
        lines.push(keys.map(k => escape(r[k])).join(','))
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `address-roster-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>
